import { GP_ACCESS_TOKEN_COOKIE } from '~/plugins/auth/constants';
import { discovery, buildEndSessionUrl } from 'openid-client';
import { keycloakLoginRedirect, buildCleanRedirectPath } from './loginRedirect';

const useAuth = () => {
  const route = useRoute();
  const { $auth } = useNuxtApp();
  const accessTokenCookie = useCookie(GP_ACCESS_TOKEN_COOKIE);
  const i18n = useI18n();
  const config = useRuntimeConfig();
  const keycloakOIDCUrl = computed(
    () => `${config.public.auth.url}/realms/${config.public.auth.realm}`,
  );

  const login = async (
    redirectPath?: string,
    additionalParameters?: Record<string, string>,
  ) => {
    const toRedirectPath =
      redirectPath != null && typeof redirectPath === 'string'
        ? redirectPath
        : buildCleanRedirectPath(route.path, route.query);

    return keycloakLoginRedirect(toRedirectPath, additionalParameters);
  };

  const logout = async (redirectPath?: string) => {
    let toRedirectPath = null;
    const middleware = route.meta.middleware;
    const isAuthProtected = Array.isArray(middleware)
      ? middleware.includes('auth')
      : middleware === 'auth';
    if (redirectPath != null && typeof redirectPath === 'string') {
      // explicit redirect path takes priority
      toRedirectPath = redirectPath;
    } else if (isAuthProtected) {
      // when logging out from an auth protected page, redirect to home page
      toRedirectPath = `/${i18n.locale.value}`;
    } else {
      // stay on page after logging out
      toRedirectPath = route.fullPath;
    }

    const keycloakIssuer = await discovery(
      new URL(keycloakOIDCUrl.value),
      config.public.auth.clientId,
    );

    const url = buildEndSessionUrl(keycloakIssuer, {
      // eslint-disable-next-line camelcase
      post_logout_redirect_uri: `${config.public.websiteUrl}${toRedirectPath}`,
      // eslint-disable-next-line camelcase
      id_token_hint: $auth.storedTokenSet.id_token,
    });

    // force removal of access token from the cookies, so after the redirect from keycloak to nuxt,
    // we do not want the server to fetch the user data and sets the store
    accessTokenCookie.value = undefined;
    $auth.clearTokenStorage();

    navigateTo(url.toString(), {
      external: true,
    });
  };

  return {
    login,
    logout,
  };
};

export default useAuth;
